import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Sparkles, Search, Filter, Calendar, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';

const ITEMS_PER_PAGE = 5;

interface Filters {
  search: string;
  date: Date | undefined;
}

interface SummaryState {
  expanded: boolean;
  id: string;
}

export default function AISummaries() {
  const [summaries, setSummaries] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedSummaries, setExpandedSummaries] = useState<Record<string, boolean>>({});
  const [filters, setFilters] = useState<Filters>({
    search: '',
    date: undefined,
  });

  const toggleSummary = (id: string) => {
    setExpandedSummaries(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  useEffect(() => {
    fetchSummaries();
  }, []);

  const fetchSummaries = async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('You must be logged in to view summaries');
      }

      const { data, error } = await supabase
        .from('sms_ai_summaries')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSummaries(data || []);
    } catch (error) {
      console.error('Error fetching summaries:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to fetch summaries');
    } finally {
      setIsLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      date: undefined,
    });
  };

  const filteredSummaries = summaries.filter((summary) => {
    const searchLower = filters.search.toLowerCase();
    const matchesSearch = 
      (summary.name?.toLowerCase().includes(searchLower) || false) ||
      summary.summary.toLowerCase().includes(searchLower);

    const matchesDate = !filters.date || 
      format(new Date(summary.created_at), 'yyyy-MM-dd') === 
      format(filters.date, 'yyyy-MM-dd');

    return matchesSearch && matchesDate;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredSummaries.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedSummaries = filteredSummaries.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  return (
    <div className="container mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold">AI Summaries</h1>
        <p className="text-muted-foreground mt-1">
          View and manage your saved AI analysis summaries
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search summaries..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-9"
              />
            </div>

            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full md:w-[200px] justify-start text-left font-normal',
                      !filters.date && 'text-muted-foreground'
                    )}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {filters.date ? format(filters.date, 'PPP') : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarPicker
                    mode="single"
                    selected={filters.date}
                    onSelect={(date) => setFilters({ ...filters, date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <Button
                variant="outline"
                onClick={resetFilters}
                className="gap-2"
                disabled={!filters.search && !filters.date}
              >
                <XCircle className="h-4 w-4" />
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <div className="space-y-6">
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
            <p className="text-muted-foreground mt-2">Loading summaries...</p>
          </div>
        ) : paginatedSummaries.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No summaries found</h3>
              <p className="text-muted-foreground">
                {summaries.length === 0
                  ? "You haven't generated any AI summaries yet. Try analyzing some issues first!"
                  : "No summaries match your current filters."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
          {paginatedSummaries.map((summary) => (
            <Card key={summary.id} className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-lg text-blue-900">
                      {summary.name}
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-blue-600">
                      {format(new Date(summary.created_at), 'PPp')}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleSummary(summary.id)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      {expandedSummaries[summary.id] ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className={`prose prose-sm max-w-none transition-all duration-300 overflow-hidden ${
                    expandedSummaries[summary.id] ? 'max-h-[1000px]' : 'max-h-24'
                  }`}>
                    {summary.summary.split('\n').map((paragraph: string, index: number) => {
                      if (paragraph.trim() === '') return null;
                      const isHeading = index === 0 || (index > 0 && summary.summary.split('\n')[index - 1] === '');
                      
                      return (
                        <p 
                          key={index} 
                          className={`${
                            isHeading 
                              ? 'font-semibold text-blue-900 text-lg mt-4' 
                              : 'text-blue-800'
                          }`}
                        >
                          {paragraph}
                        </p>
                      );
                    })}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-blue-600">
                    <div className="flex items-center gap-1">
                      <Filter className="h-4 w-4" />
                      <span>{summary.issue_count} issues analyzed</span>
                    </div>
                    {summary.filters && Object.values(summary.filters).some(v => v !== null) && (
                      <div className="flex items-center gap-2">
                        <span>•</span>
                        <span>Filtered analysis</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t pt-4">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}